/**
 * Settings for AGENCY-ONLY sessions (invite-only employees) — 2026-09-07.
 *
 * Every existing `/settings/*` page is seller-account-centric: General loads
 * `users/settings` + the custom OpenAI key, Profile edits the seller
 * registration and changes the password through the seller login — all with
 * the seller JWT an invited employee never holds. On live that surfaced as a
 * 401 on `users/settings` and an "Error Fetching Data" card.
 *
 * This page is what an employee can actually act on: who they are (from
 * `/api/v1/me`), which organisation and brands they belong to, a password
 * reset through the employee flow, and the jump to their personal MCP setup.
 * Rendered for `general/*` and `profile/*` when the session is agency-only;
 * seller sessions keep the original pages.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../store/store';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { HOME_ROUTE } from '../../../lib/constants/route';
import { employeeForgotPassword } from '../../../lib/api/identity/identity-api';
import { useBrandLabels } from '../../../lib/identity/brand-mapping';
import { submitLogToBugsnag } from '../../../lib/api/log';
import '../../../styles/pages/settings/settings.scss';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  team_lead: 'Team Lead',
  analyst: 'Analyst',
  read_only: 'Read Only',
};

const EmployeeSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const identity = useSelector(
    (state: RootState) => state.auth?.agency?.employeeIdentity
  );
  const allShops = useSelector(
    (state: RootState) => state.user?.profile?.all_shops ?? []
  );
  const labels = useBrandLabels();
  const [resetState, setResetState] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');

  const email = identity?.email ?? '';
  const name = identity?.name ?? '';
  const role = identity?.role ?? '';
  const brandCount = identity?.accessible_shops?.length ?? allShops.length;

  const sendReset = async () => {
    if (!email) return;
    setResetState('sending');
    try {
      await employeeForgotPassword(email);
      setResetState('sent');
    } catch (err) {
      submitLogToBugsnag('error', `employee password reset request: ${err}`);
      setResetState('error');
    }
  };

  const rows: Array<[string, string]> = [
    [t('Name'), name || '—'],
    [t('Email'), email || '—'],
    [t('EmployeeSettingsRole'), ROLE_LABELS[role] ?? role ?? '—'],
    [t('EmployeeSettingsBrandAccess'), `${brandCount} ${labels.shopLowerPlural}`],
  ];

  return (
    <SubPageInnerContentHolder
      title={t('EmployeeSettingsTitle')}
      subTitle={t('EmployeeSettingsSubtitle')}
      backBtnLink={HOME_ROUTE}
    >
      <SettingsCommonCard
        mainTitle={t('EmployeeSettingsAccountCard')}
        subTitle={t('EmployeeSettingsAccountCardHint')}
      >
        <dl className='employeeSettings__rows'>
          {rows.map(([label, value]) => (
            <div className='employeeSettings__row' key={label}>
              <dt>{label}</dt>
              <dd data-testid={`employee-settings-${label}`}>{value}</dd>
            </div>
          ))}
        </dl>
      </SettingsCommonCard>

      <SettingsCommonCard
        mainTitle={t('ChangePassword')}
        subTitle={t('EmployeeSettingsPasswordHint')}
      >
        <div className='employeeSettings__actions'>
          <MainSquareButton
            value={
              resetState === 'sent'
                ? t('EmployeeSettingsResetSent')
                : t('SendResetLink')
            }
            onClick={sendReset}
            loading={resetState === 'sending'}
            disabled={!email || resetState === 'sent'}
          />
          {resetState === 'error' && (
            <p className='employeeSettings__error'>
              {t('EmployeeSettingsResetFailed')}
            </p>
          )}
        </div>
      </SettingsCommonCard>

      <SettingsCommonCard
        mainTitle={t('EmployeeSettingsMcpCard')}
        subTitle={t('EmployeeSettingsMcpHint')}
        btnText={t('EmployeeSettingsMcpButton')}
        onClick={() => navigate('/my-mcp-setup')}
      />
    </SubPageInnerContentHolder>
  );
};

export default EmployeeSettings;
